import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import '../Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const StationTemperature = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pours, setPours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPour, setSelectedPour] = useState(null);
  const [selectedReading, setSelectedReading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [devicesData, setDevicesData] = useState([
    { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
    { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
    { deviceName: '', t1: '', t2: '', t3: '', t4: '' }
  ]);
  const [comment, setComment] = useState('');

  const { stationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stationName = location.state?.stationName || stationId;

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const fetchPours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/pours`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          status: 'approved',
          station: stationName
        }
      });

      if (response.data.success) {
        setPours(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pours:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (pourId) => {
    setExpandedRows(prev => ({
      ...prev,
      [pourId]: !prev[pourId]
    }));
  };

  const handleOpenModal = async (pour) => {
    setSelectedPour(pour);
    
    if (pour.deviceNames && pour.deviceNames.length === 3) {
      setDevicesData([
        { deviceName: pour.deviceNames[0], t1: '', t2: '', t3: '', t4: '' },
        { deviceName: pour.deviceNames[1], t1: '', t2: '', t3: '', t4: '' },
        { deviceName: pour.deviceNames[2], t1: '', t2: '', t3: '', t4: '' }
      ]);
    } else {
      setDevicesData([
        { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
        { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
        { deviceName: '', t1: '', t2: '', t3: '', t4: '' }
      ]);
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPour(null);
    setComment('');
    setDevicesData([
      { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
      { deviceName: '', t1: '', t2: '', t3: '', t4: '' },
      { deviceName: '', t1: '', t2: '', t3: '', t4: '' }
    ]);
  };

  const handleDeviceChange = (index, field, value) => {
    const newDevicesData = [...devicesData];
    newDevicesData[index][field] = value;
    setDevicesData(newDevicesData);
  };

  const handleSaveReading = async () => {
    try {
      const hasExistingDevices = selectedPour?.deviceNames && selectedPour.deviceNames.length === 3;
      
      if (hasExistingDevices) {
        for (let i = 0; i < 3; i++) {
          const device = devicesData[i];
          if (!device.t1 || !device.t2 || !device.t3 || !device.t4) {
            toast.error(`الرجاء إدخال جميع القراءات للجهاز ${i + 1}`);
            return;
          }
        }
      } else {
        for (let i = 0; i < 3; i++) {
          const device = devicesData[i];
          if (!device.deviceName.trim()) {
            toast.error(`الرجاء إدخال اسم الجهاز ${i + 1}`);
            return;
          }
          if (!device.t1 || !device.t2 || !device.t3 || !device.t4) {
            toast.error(`الرجاء إدخال جميع القراءات للجهاز ${i + 1}`);
            return;
          }
        }
      }

      setSaving(true);
      const token = localStorage.getItem('token');
      
      const now = new Date();
      const readingDate = now.toISOString().split('T')[0];
      const readingTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      const payload = {
        readingDate: readingDate,
        readingTime: readingTime,
        devices: devicesData.map(device => ({
          deviceName: device.deviceName.trim(),
          t1: parseFloat(device.t1),
          t2: parseFloat(device.t2),
          t3: parseFloat(device.t3),
          t4: parseFloat(device.t4)
        })),
        comment: comment.trim()
      };
      
      const response = await axios.post(
        `${API_URL}/pours/${selectedPour._id}/temperature`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('تم حفظ القراءة بنجاح');
        handleCloseModal();
        fetchPours();
      }
    } catch (error) {
      console.error('Error saving reading:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || 'حدث خطأ أثناء حفظ القراءة');
      }
    } finally {
      setSaving(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCurrentUser(response.data.data.user);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const canEditDelete = () => {
    if (!currentUser) return false;
    return ['admin', 'engineer', 'senior', 'supervisor'].includes(currentUser.role);
  };

  const handleEditReading = (pour, reading) => {
    setSelectedPour(pour);
    setSelectedReading(reading);
    setDevicesData(reading.devices.map(d => ({
      deviceName: d.deviceName,
      t1: d.t1 || '',
      t2: d.t2 || '',
      t3: d.t3 || '',
      t4: d.t4 || ''
    })));
    setComment(reading.comment || '');
    setShowEditModal(true);
  };

  const handleUpdateReading = async () => {
    if (!selectedPour || !selectedReading) return;

    const emptyDevices = devicesData.filter(d => 
      !d.deviceName.trim() || 
      (d.t1 === '' && d.t2 === '' && d.t3 === '' && d.t4 === '')
    );

    if (emptyDevices.length > 0) {
      toast.error('يرجى ملء جميع بيانات الأجهزة');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const deviceNames = devicesData.map(d => d.deviceName);

      await axios.put(
        `${API_URL}/pours/${selectedPour._id}/temperature/${selectedReading._id}`,
        {
          devices: devicesData,
          deviceNames: deviceNames,
          comment: comment
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('تم تحديث القراءة بنجاح');
      setShowEditModal(false);
      setSelectedPour(null);
      setSelectedReading(null);
      setComment('');
      fetchPours();
    } catch (error) {
      console.error('Error updating reading:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء تحديث القراءة');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReading = async (pourId, readingId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه القراءة؟')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/pours/${pourId}/temperature/${readingId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('تم حذف القراءة بنجاح');
      fetchPours();
    } catch (error) {
      console.error('Error deleting reading:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء حذف القراءة');
    }
  };

  useEffect(() => {
    fetchPours();
    fetchCurrentUser();
  }, [stationName]);

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/pours/temperature')}
              style={{
                background: '#64748b',
                color: 'white',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              ← رجوع
            </button>
            <h1 style={{ margin: 0, fontSize: '20px' }}>🌡️ {stationName}</h1>
          </div>
        </div>

        <div className="dashboard-main">
          <style>{`
            * {
              box-sizing: border-box;
            }

            .station-container {
              padding: 15px;
              max-width: 100%;
              margin: 0 auto;
            }

            .stats-row {
              display: flex;
              gap: 10px;
              margin-bottom: 15px;
              flex-wrap: wrap;
            }

            .stat-box {
              flex: 1;
              min-width: 120px;
              background: white;
              padding: 12px;
              border-radius: 10px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.08);
              text-align: center;
            }

            .stat-number {
              font-size: 24px;
              font-weight: 800;
              color: #ef4444;
              margin: 0;
            }

            .stat-label {
              font-size: 12px;
              color: #64748b;
              margin: 4px 0 0 0;
            }

            .table-card {
              background: white;
              border-radius: 12px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow: hidden;
            }

            .table-header {
              background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
              padding: 15px;
              color: white;
            }

            .table-header h2 {
              margin: 0;
              font-size: 18px;
              font-weight: 700;
            }

            .table-wrapper {
              width: 100%;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

            .pours-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            .pours-table thead {
              background: #1e293b;
              color: white;
              position: sticky;
              top: 0;
              z-index: 10;
            }

            .pours-table th {
              padding: 10px 6px;
              text-align: center;
              font-weight: 600;
              white-space: nowrap;
              font-size: 11px;
            }

            .pours-table tbody tr {
              border-bottom: 1px solid #e2e8f0;
            }

            .pours-table tbody tr:hover {
              background: #f8fafc;
            }

            .pours-table td {
              padding: 10px 6px;
              text-align: center;
              color: #334155;
              white-space: nowrap;
            }

            .btn-small {
              padding: 6px 10px;
              border: none;
              border-radius: 6px;
              font-size: 11px;
              font-weight: 600;
              cursor: pointer;
              white-space: nowrap;
            }

            .btn-view {
              background: #3b82f6;
              color: white;
            }

            .btn-add {
              background: #10b981;
              color: white;
            }

            .btn-add:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            .expanded-content {
              padding: 15px;
              background: #f1f5f9;
            }

            .reading-group {
              background: white;
              border-radius: 8px;
              padding: 12px;
              margin-bottom: 10px;
              border: 2px solid #e2e8f0;
            }

            .reading-time {
              font-size: 13px;
              font-weight: 700;
              color: #1e293b;
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              gap: 6px;
            }

            .reading-user {
              font-size: 11px;
              color: #64748b;
              margin-bottom: 8px;
            }

            .devices-row {
              display: flex;
              gap: 8px;
              flex-wrap: wrap;
            }

            .device-box {
              flex: 1;
              min-width: 100px;
              background: #f8fafc;
              padding: 8px;
              border-radius: 6px;
              border: 1px solid #e2e8f0;
            }

            .device-name {
              font-size: 11px;
              font-weight: 600;
              color: #475569;
              margin-bottom: 6px;
            }

            .temp-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 4px;
            }

            .temp-item {
              background: #ef4444;
              color: white;
              padding: 4px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
              text-align: center;
            }

            .empty-msg {
              text-align: center;
              padding: 40px 20px;
              color: #94a3b8;
            }

            /* Modal */
            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.6);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10000;
              padding: 15px;
            }

            .modal-content {
              background: white;
              border-radius: 12px;
              padding: 20px;
              max-width: 100%;
              width: 100%;
              max-height: 90vh;
              overflow-y: auto;
            }

            .modal-title {
              font-size: 18px;
              font-weight: 700;
              margin: 0 0 15px 0;
              color: #1e293b;
              border-bottom: 2px solid #ef4444;
              padding-bottom: 10px;
            }

            .pour-info {
              background: #fef2f2;
              padding: 10px;
              border-radius: 8px;
              margin-bottom: 15px;
              font-size: 12px;
              color: #475569;
            }

            .device-section {
              background: #f8fafc;
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              padding: 12px;
              margin-bottom: 12px;
            }

            .device-header {
              font-size: 14px;
              font-weight: 700;
              color: #ef4444;
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 1px solid #e2e8f0;
            }

            .input-group {
              margin-bottom: 10px;
            }

            .input-label {
              display: block;
              font-size: 11px;
              font-weight: 600;
              color: #475569;
              margin-bottom: 4px;
            }

            .modal-input {
              width: 100%;
              padding: 8px 10px;
              border: 2px solid #e2e8f0;
              border-radius: 6px;
              font-size: 14px;
              font-family: inherit;
            }

            .modal-input:focus {
              outline: none;
              border-color: #ef4444;
            }

            .modal-input:disabled {
              background: #f1f5f9;
            }

            .readings-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }

            .modal-actions {
              display: flex;
              gap: 10px;
              margin-top: 15px;
            }

            .modal-btn {
              flex: 1;
              padding: 12px;
              border: none;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 700;
              cursor: pointer;
            }

            .btn-cancel {
              background: #f1f5f9;
              color: #475569;
            }

            .btn-save {
              background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
              color: white;
            }

            .btn-save:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            @media (max-width: 768px) {
              .dashboard-header h1 {
                font-size: 16px;
              }

              .stat-box {
                min-width: 100px;
              }

              .stat-number {
                font-size: 20px;
              }

              .stat-label {
                font-size: 11px;
              }

              .pours-table {
                font-size: 11px;
              }

              .pours-table th,
              .pours-table td {
                padding: 8px 4px;
              }

              .btn-small {
                padding: 5px 8px;
                font-size: 10px;
              }
            }
          `}</style>

          <div className="station-container">
            <div className="stats-row">
              <div className="stat-box">
                <h3 className="stat-number">{pours.length}</h3>
                <p className="stat-label">إجمالي الصبات</p>
              </div>
              <div className="stat-box">
                <h3 className="stat-number">
                  {pours.filter(p => p.temperatureReadings?.length > 0).length}
                </h3>
                <p className="stat-label">لها قراءات</p>
              </div>
              <div className="stat-box">
                <h3 className="stat-number">
                  {pours.filter(p => !p.temperatureReadings || p.temperatureReadings.length === 0).length}
                </h3>
                <p className="stat-label">بدون قراءات</p>
              </div>
            </div>

            <div className="table-card">
              <div className="table-header">
                <h2>📋 جدول الصبات</h2>
              </div>

              {loading ? (
                <div className="empty-msg">⏳ جاري التحميل...</div>
              ) : pours.length === 0 ? (
                <div className="empty-msg">لا توجد صبات لهذه المحطة</div>
              ) : (
                <div className="table-wrapper">
                  <table className="pours-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>التاريخ</th>
                        <th>الموقع</th>
                        <th>Mix</th>
                        <th>القراءات</th>
                        <th>إجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pours.map((pour, index) => (
                        <React.Fragment key={pour._id}>
                          <tr>
                            <td>{index + 1}</td>
                            <td>{formatDate(pour.castingDate)}</td>
                            <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {pour.location}
                            </td>
                            <td>{pour.mix}</td>
                            <td>
                              <span style={{ 
                                background: pour.temperatureReadings?.length > 0 ? '#10b981' : '#94a3b8',
                                color: 'white',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                {pour.temperatureReadings?.length || 0}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                {pour.temperatureReadings?.length > 0 && (
                                  <button 
                                    className="btn-small btn-view"
                                    onClick={() => toggleRow(pour._id)}
                                  >
                                    {expandedRows[pour._id] ? 'إخفاء' : 'عرض'}
                                  </button>
                                )}
                                <button 
                                  className="btn-small btn-add"
                                  onClick={() => handleOpenModal(pour)}
                                  disabled={loading}
                                >
                                  + إضافة
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedRows[pour._id] && pour.temperatureReadings?.length > 0 && (
                            <tr>
                              <td colSpan="6" style={{ padding: 0 }}>
                                <div className="expanded-content">
                                  {pour.temperatureReadings.map((reading, idx) => (
                                    <div key={idx} className="reading-group">
                                      <div className="reading-time">
                                        🕐 {formatDate(reading.readingDate)} - {reading.readingTime}
                                      </div>
                                      {reading.addedBy && (
                                        <div className="reading-user">
                                          👤 {reading.addedBy.fullName} ({reading.addedBy.role})
                                        </div>
                                      )}
                                      <div className="devices-row">
                                        {reading.devices?.map((device, deviceIdx) => (
                                          <div key={deviceIdx} className="device-box">
                                            <div className="device-name">🌡️ {device.deviceName}</div>
                                            <div className="temp-grid">
                                              <div className="temp-item">T1: {device.t1}°</div>
                                              <div className="temp-item">T2: {device.t2}°</div>
                                              <div className="temp-item">T3: {device.t3}°</div>
                                              <div className="temp-item">T4: {device.t4}°</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {reading.comment && (
                                        <div style={{ 
                                          marginTop: '8px', 
                                          padding: '8px', 
                                          background: '#fef3c7', 
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          color: '#92400e'
                                        }}>
                                          💬 {reading.comment}
                                        </div>
                                      )}
                                      {canEditDelete() && (
                                        <div style={{ 
                                          marginTop: '8px', 
                                          display: 'flex', 
                                          gap: '6px',
                                          justifyContent: 'flex-end'
                                        }}>
                                          <button 
                                            className="btn-small"
                                            onClick={() => handleEditReading(pour, reading)}
                                            style={{ 
                                              background: '#3b82f6',
                                              color: 'white'
                                            }}
                                          >
                                            ✏️ تعديل
                                          </button>
                                          <button 
                                            className="btn-small"
                                            onClick={() => handleDeleteReading(pour._id, reading._id)}
                                            style={{ 
                                              background: '#ef4444',
                                              color: 'white'
                                            }}
                                          >
                                            🗑️ حذف
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedPour && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">إضافة قراءة حرارة جديدة</h3>
            
            <div className="pour-info">
              <strong>الصبة:</strong> {selectedPour.location}<br />
              <strong>التاريخ:</strong> {formatDate(selectedPour.castingDate)}<br />
              <strong>Mix:</strong> {selectedPour.mix}
            </div>

            {devicesData.map((device, index) => (
              <div key={index} className="device-section">
                <div className="device-header">🌡️ الجهاز {index + 1}</div>
                
                <div className="input-group">
                  <label className="input-label">اسم الجهاز:</label>
                  <input
                    type="text"
                    className="modal-input"
                    value={device.deviceName}
                    onChange={(e) => handleDeviceChange(index, 'deviceName', e.target.value)}
                    placeholder={`مثال: Thermometer ${index + 1}`}
                    disabled={selectedPour.deviceNames && selectedPour.deviceNames.length === 3}
                  />
                </div>

                <div className="readings-grid">
                  <div className="input-group">
                    <label className="input-label">T1 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t1}
                      onChange={(e) => handleDeviceChange(index, 't1', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T2 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t2}
                      onChange={(e) => handleDeviceChange(index, 't2', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T3 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t3}
                      onChange={(e) => handleDeviceChange(index, 't3', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T4 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t4}
                      onChange={(e) => handleDeviceChange(index, 't4', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="input-group" style={{ marginTop: '15px' }}>
              <label className="input-label">💬 ملاحظات (اختياري):</label>
              <textarea
                className="modal-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="أضف أي ملاحظات هنا..."
                rows="3"
                style={{ resize: 'vertical', minHeight: '60px' }}
              />
            </div>

            <div className="modal-actions">
              <button className="modal-btn btn-cancel" onClick={handleCloseModal}>
                إلغاء
              </button>
              <button 
                className="modal-btn btn-save" 
                onClick={handleSaveReading}
                disabled={saving}
              >
                {saving ? '⏳ جاري الحفظ...' : '💾 حفظ القراءة'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedReading && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">✏️ تعديل قراءة الحرارة</h3>
            
            <div className="pour-info">
              <strong>الصبة:</strong> {selectedPour.location}<br />
              <strong>التاريخ:</strong> {formatDate(selectedPour.castingDate)}<br />
              <strong>وقت القراءة:</strong> {formatDate(selectedReading.readingDate)} - {selectedReading.readingTime}
            </div>

            {devicesData.map((device, index) => (
              <div key={index} className="device-section">
                <div className="device-header">🌡️ الجهاز {index + 1}</div>
                
                <div className="input-group">
                  <label className="input-label">اسم الجهاز:</label>
                  <input
                    type="text"
                    className="modal-input"
                    value={device.deviceName}
                    onChange={(e) => handleDeviceChange(index, 'deviceName', e.target.value)}
                    placeholder={`مثال: Thermometer ${index + 1}`}
                  />
                </div>

                <div className="readings-grid">
                  <div className="input-group">
                    <label className="input-label">T1 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t1}
                      onChange={(e) => handleDeviceChange(index, 't1', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T2 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t2}
                      onChange={(e) => handleDeviceChange(index, 't2', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T3 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t3}
                      onChange={(e) => handleDeviceChange(index, 't3', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">T4 (°C):</label>
                    <input
                      type="number"
                      step="0.1"
                      className="modal-input"
                      value={device.t4}
                      onChange={(e) => handleDeviceChange(index, 't4', e.target.value)}
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="input-group" style={{ marginTop: '15px' }}>
              <label className="input-label">💬 ملاحظات (اختياري):</label>
              <textarea
                className="modal-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="أضف أي ملاحظات هنا..."
                rows="3"
                style={{ resize: 'vertical', minHeight: '60px' }}
              />
            </div>

            <div className="modal-actions">
              <button className="modal-btn btn-cancel" onClick={() => setShowEditModal(false)}>
                إلغاء
              </button>
              <button 
                className="modal-btn btn-save" 
                onClick={handleUpdateReading}
                disabled={saving}
              >
                {saving ? '⏳ جاري التحديث...' : '💾 حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationTemperature;